import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Clock, CloudOff, Loader2, Receipt, Repeat, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import { AppButton, EmptyState, ScreenHeader, Segmented } from "@/components/AppUI";
import { euro, isPast, papi, savedOrderIds, statusInfo, timeLabel, SIZE_LABELS } from "@/lib/pizzeria";

export const StatusPill = ({ status, testId }) => {
  const [label, bg, fg] = statusInfo(status);
  return <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: bg, color: fg }} data-testid={testId}>{label}</span>;
};

const summary = (o) => (o.items || []).map((x) => `${x.quantity}× ${x.name}${x.size ? ` (${SIZE_LABELS[x.size] || x.size})` : ""}`).join(", ");

const OrderCard = ({ o, onReorder }) => {
  const nav = useNavigate();
  return (
    <div className="rounded-[20px] p-4 space-y-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }} data-testid={`order-card-${o.id}`}>
      <button onClick={() => nav(`/order/${o.id}`)} className="w-full text-left space-y-2">
        <div className="flex justify-between items-center"><p className="text-base font-medium">Ordine #{o.code}</p><StatusPill status={o.status} testId={`order-status-${o.id}`} /></div>
        <p className="flex items-center gap-1.5 text-sm" style={{ color: "var(--muted-c)" }}><Clock className="h-4 w-4" />Ritiro {timeLabel(o.pickup_time)}</p>
        <p className="text-sm leading-5 line-clamp-2">{summary(o)}</p>
        <div className="flex justify-between items-center"><span className="text-base font-medium">{euro(o.total)}</span><ChevronRight className="h-[18px] w-[18px]" style={{ color: "var(--muted-c)" }} /></div>
      </button>
      {isPast(o) && (
        <div className="flex gap-2">
          <AppButton small variant="dark" icon={Repeat} title="Riordina" onClick={() => onReorder(o)} testId={`order-reorder-button-${o.id}`} className="flex-1" />
          {o.status === "ritirato" && !o.has_feedback && <AppButton small variant="outline" icon={Star} title="Recensisci" onClick={() => nav(`/feedback/${o.id}`)} testId={`order-feedback-button-${o.id}`} className="flex-1" />}
        </div>
      )}
    </div>
  );
};

export default function MyOrders() {
  const { user } = useAuth();
  const { add } = useCart();
  const { products, settings } = useShop();
  const nav = useNavigate();
  const [tab, setTab] = useState("active");
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const ids = savedOrderIds();
      const [mine, guest] = await Promise.all([
        user ? papi.get("/orders/mine").then((r) => r.data) : [],
        ids.length ? papi.post("/orders/lookup", { ids }).then((r) => r.data) : [],
      ]);
      const all = [...mine, ...guest].filter((o, i, a) => a.findIndex((x) => x.id === o.id) === i);
      setOrders(all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      setError(false);
    } catch {
      setError(true);
    }
  }, [user]);

  useEffect(() => {
    if (user !== null) {
      load();
      const t = setInterval(load, 10000);
      return () => clearInterval(t);
    }
  }, [load, user]);

  const reorder = (o) => {
    const extrasById = Object.fromEntries((settings?.extras || []).map((x) => [x.id, x.name]));
    const ok = (o.items || []).filter((x) => x.product_id === "builder" || (products || []).some((p) => p.id === x.product_id));
    if (!ok.length) return toast.error("I prodotti di questo ordine non sono più disponibili");
    ok.forEach((x) => add({ product_id: x.product_id, name: x.name, image: x.image || "", size: x.size, quantity: x.quantity, unit_price: x.unit_price, extras: x.extras || [], extra_names: (x.extras || []).map((e) => extrasById[e]).filter(Boolean), removed: x.removed || [], ...(x.builder && { builder: x.builder, ingredients: x.ingredients }) }));
    toast.success(ok.length < o.items.length ? `Ordine aggiunto al carrello (${o.items.length - ok.length} prodotti non più disponibili)` : "Ordine aggiunto al carrello");
    nav("/carrello");
  };

  const list = (orders || []).filter((o) => (tab === "past" ? isPast(o) : !isPast(o)));
  return (
    <div className="flex-1" data-testid="my-orders-page">
      <ScreenHeader title="I miei ordini" fallback="/profilo" />
      <div className="px-4 pt-3 pb-3" style={{ borderBottom: "1px solid var(--border-c)" }}>
        <Segmented testIdPrefix="orders-tab" value={tab} onChange={setTab} options={[{ key: "active", label: "Attivi" }, { key: "past", label: "Passati" }]} />
      </div>
      {error && !orders ? (
        <EmptyState icon={CloudOff} title="Errore nel caricamento"><AppButton title="Riprova" onClick={load} testId="orders-retry" /></EmptyState>
      ) : !orders ? (
        <Loader2 className="h-6 w-6 animate-spin mx-auto mt-12" style={{ color: "var(--brand)" }} />
      ) : (
        <div className="p-4 pb-6 space-y-3">
          {list.map((o) => <OrderCard key={o.id} o={o} onReorder={reorder} />)}
          {!list.length && <EmptyState icon={Receipt} title={tab === "past" ? "Non hai ancora ordini passati" : "Nessun ordine in corso"} subtitle="Ordina dal menù e ritira in pizzeria." testId="orders-empty"><AppButton title="Vai al menù" onClick={() => nav("/")} testId="orders-go-menu" /></EmptyState>}
        </div>
      )}
    </div>
  );
}
